import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import {
  Button,
} from '@components/common/Forms';
import { useBjForm } from '@hooks/useBjForm';
import { useEffect } from 'react';
import { BillingDataForm } from '@components/templates/billing-data/BillingDataForm';

interface ContinueOrderProps {
  locale: Locale,
  successCallback?: () => void,
  defaultCompany?: boolean;
  employerData: ApiSchema<'CompanyRead'>;
}

export const AddBillingDataModal = NiceModal.create((props: ContinueOrderProps) => {
  const {
    locale, successCallback, defaultCompany = true, employerData,
  } = props;
  // Use a hook to manage the modal state
  const modal = useModal();

  // Close modal
  const closeModal = () => {
    void modal.hide();
  };

  // Translation
  const { t } = useTranslation('common');

  const {
    record, handleSubmit, formStatus, isPosting, updateFormElements,
  } = useBjForm({
    submitURL: '/v1/company',
    submitMethod: 'POST',
  });

  useEffect(() => {
    if (formStatus.actionStatus === 'success') {
      void modal.hide();
      if (successCallback) successCallback();
    }
  }, [formStatus, modal, successCallback]);

  // Sometimes the employer data is not available when the form is rendered
  // so we need to update the form elements when the employer data is available
  useEffect(() => {
    if (employerData) updateFormElements();
  }, [employerData, updateFormElements]);

  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="lg"
      header={t('account.consumption.billing_data')}
    >
      <Modal.Body className="!p-0">
        {employerData && (
        <form onSubmit={handleSubmit} noValidate>
          <div className="bg-surface-100 p-6">
            <BillingDataForm
              customer={employerData as ApiSchema<'CompanyWrite'>}
              locale={locale}
              record={record}
              defaultCompany={defaultCompany}
              updateFormElements={updateFormElements}
            />
          </div>
          <div className="border-t bg-surface px-6 py-4 text-right">
            <Button
              size="sm"
              color="secondary"
              rounding="full"
              type="submit"
              className="!px-7"
              isLoading={isPosting}
              disabled={isPosting}
            >
              {t('order.choosePayment.cta')}
            </Button>
          </div>
        </form>
        )}
      </Modal.Body>
    </Modal>
  );
});

export const showAddBillingDataModal = (
  locale: Locale,
  employerData: ApiSchema<'CompanyRead'>,
  successCallback?: () => void,
  defaultCompany?: boolean,
) => {
  void NiceModal.show(AddBillingDataModal, {
    locale, successCallback, defaultCompany, employerData,
  });
};
